import React, { useId } from 'react';
import styled, { css, keyframes } from 'styled-components';

import { TCustomerInsights } from 'containers/Intelligence/i18n/TModules';
import { tokens } from 'containers/Intelligence/theme';
import useMultiLanguageTranslation from 'hooks/useMultiLanguageTranslation';

export type HandoffResultStatus = 'sending' | 'success' | 'failed';

export type HandoffResultActionTone = 'primary' | 'secondary';

export interface IHandoffResultMetaRow {
  /** Caller-supplied wording: the slide-out owns the handoff receipt labels, not this component. */
  label: string;
  /** Pre-formatted by the caller — ids, counts and timestamps are all formatted upstream. */
  value: string;
}

export interface IHandoffResultAction {
  label: string;
  onClick: () => void;
  tone?: HandoffResultActionTone;
  disabled?: boolean;
}

export interface IHandoffResultStateProps {
  status: HandoffResultStatus;
  /** Overrides the shared body copy: the sending screen names the metric and customer counts. */
  body?: string;
  /** The success receipt rows. Omitted rows are not drawn — no placeholder receipt is invented. */
  meta?: IHandoffResultMetaRow[];
  actions?: IHandoffResultAction[];
  /** Error reference line under the failed actions — payload, never authored here. */
  footnote?: string;
  /** 0–1 while sending. Omitted leaves the bar indeterminate rather than guessing a share. */
  progress?: number;
  className?: string;
}

const ICON_SIZE = '60px';
const SPINNER_SIZE = '52px';
const PROGRESS_HEIGHT = '4px';
/**
 * The success and failed frames constrain their column with the file's own `width/320` variable, on
 * the body, the receipt and the actions alike (615:2753 / 615:2755 / 615:2777, and 615:2868 /
 * 615:2870 on the failed frame), and centre it in the 520 panel. The SENDING frame carries no cap —
 * every one of its rows is 476 wide (615:2785 … 615:2794) — so the rule below is scoped by status
 * rather than applied to the shell. PLA-8815.
 */
const RESULT_COLUMN = '320px';

const COPY_KEY: Record<HandoffResultStatus, string> = {
  sending: 'Sending',
  success: 'Success',
  failed: 'Failed'
};

const ICON_HUE: Record<HandoffResultStatus, string> = {
  sending: tokens.color['accent-purple'],
  success: tokens.color.positive,
  failed: tokens.color.negative
};

const ICON_TINT: Record<HandoffResultStatus, string> = {
  sending: 'transparent',
  success: tokens.color['tint-positive-bg'],
  failed: tokens.color['tint-negative-bg']
};

const ACTION_HUE: Record<HandoffResultStatus, string> = {
  sending: tokens.color['accent-purple'],
  success: tokens.color['accent-purple'],
  failed: tokens.color.negative
};

const spin = keyframes`
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
`;

const sweep = keyframes`
  0% {
    transform: translateX(-100%);
  }
  100% {
    transform: translateX(250%);
  }
`;

const Result = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: ${tokens.space['24']} ${tokens.space['20']};
  text-align: center;

  /* Safari < 14.1 has no flex gap (conventions/components/cross-browser.md § 5).

     Doubled, not because the rhythm is special but because Body and Footnote are p elements that
     reset their own margin at the same specificity and are injected after this rule: the tie went
     to the reset and the footnote measured 0.0px off the actions in both engines at both widths. */
  && > * + * {
    margin-top: ${tokens.space['16']};
  }

  /* The file's rhythm is not uniform. div.resultIcon:margin carries a 20px margin-bottom and
     div.resultActions:margin a 24px margin-top, on the success (615:2747 / 615:2777) and the
     failed (615:2862 / 615:2870) frame alike. */
  && > [data-testid='handoff-result-state-icon'] + * {
    margin-top: ${tokens.space['20']};
  }

  && > [data-testid='handoff-result-state-actions'] {
    margin-top: ${tokens.space['24']};
  }

  &[data-status='success'] > *,
  &[data-status='failed'] > * {
    max-width: ${RESULT_COLUMN};
  }
`;

const IconBadge = styled.div<{ $status: HandoffResultStatus }>`
  display: flex;
  width: ${ICON_SIZE};
  height: ${ICON_SIZE};
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  background: ${({ $status }) => ICON_TINT[$status]};
  color: ${({ $status }) => ICON_HUE[$status]};
  font-size: 26px;
  line-height: 31px;
`;

const Spinner = styled.div`
  width: ${SPINNER_SIZE};
  height: ${SPINNER_SIZE};
  border: 2px solid ${tokens.color['border-strong']};
  border-radius: 50%;
  border-top-color: ${tokens.color['accent-purple']};
  animation: ${spin} 900ms linear infinite;
`;

const Message = styled.div`
  width: 100%;

  /* Doubled for the same reason as the one on Result: Body resets its own margin at this
     specificity and wins the tie, so the file's 8px title-to-body step (h3:margin 615:2750 /
     615:2865) never landed and the two boxes measured flush. */
  && > * + * {
    margin-top: ${tokens.space['8']};
  }
`;

const Title = styled.h3`
  margin: 0;
  color: ${tokens.color['text-title']};
  font-size: ${tokens.type['card-title-size']};
  font-weight: ${tokens.type['card-title-weight']};
  line-height: ${tokens.type['card-title-line']};
`;

const Body = styled.p`
  margin: 0;
  color: ${tokens.color['text-muted']};
  font-size: ${tokens.type['body-size']};
  font-weight: ${tokens.type['body-weight']};
  line-height: ${tokens.type['body-line']};
`;

const Track = styled.div`
  width: 100%;
  height: ${PROGRESS_HEIGHT};
  border-radius: ${tokens.radius.xs};
  background: ${tokens.color['track-bg']};
  overflow: hidden;
`;

const Fill = styled.div<{ $indeterminate: boolean; $share: number }>`
  height: 100%;
  border-radius: inherit;
  background: ${tokens.color['accent-purple']};
  width: ${({ $indeterminate, $share }) => ($indeterminate ? '40%' : `${$share * 100}%`)};
  ${({ $indeterminate }) =>
    $indeterminate
      ? css`
          animation: ${sweep} 1200ms ease-in-out infinite;
        `
      : ''}
`;

const Meta = styled.dl`
  width: 100%;
  margin: 0;
  padding: ${tokens.space['12']} ${tokens.space['16']};
  border-radius: ${tokens.radius.md};
  background: ${tokens.color['surface-card-alt']};
  text-align: left;

  > * + * {
    margin-top: ${tokens.space['8']};
  }
`;

const MetaRow = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
`;

const MetaLabel = styled.dt`
  margin: 0;
  color: ${tokens.color['text-muted']};
  font-size: ${tokens.type['body-size']};
  font-weight: ${tokens.type['body-weight']};
  line-height: ${tokens.type['body-line']};
`;

const MetaValue = styled.dd`
  margin: 0;
  color: ${tokens.color['text-title']};
  font-size: ${tokens.type['body-size']};
  font-weight: ${tokens.type['card-title-weight']};
  line-height: ${tokens.type['body-line']};
  text-align: right;
`;

const Actions = styled.div`
  display: flex;
  justify-content: center;

  > * + * {
    margin-left: ${tokens.space['12']};
  }
`;

const ActionButton = styled.button<{ $tone: HandoffResultActionTone; $hue: string }>`
  padding: ${tokens.space['12']} ${tokens.space['24']};
  border: 1px solid
    ${({ $tone, $hue }) => ($tone === 'primary' ? $hue : tokens.color['border-strong'])};
  border-radius: ${tokens.radius.md};
  background: ${({ $tone, $hue }) => ($tone === 'primary' ? $hue : 'transparent')};
  color: ${({ $tone }) =>
    $tone === 'primary' ? tokens.color['on-accent'] : tokens.color['text-secondary']};
  cursor: pointer;
  font-size: ${tokens.type['body-size']};
  font-weight: ${tokens.type['card-title-weight']};
  line-height: ${tokens.type['body-line']};

  &:disabled {
    cursor: not-allowed;
    opacity: 0.6;
  }

  &:focus-visible {
    outline: none;
    box-shadow: ${tokens.elevation['focus-ring']};
  }
`;

const Footnote = styled.p`
  margin: 0;
  color: ${tokens.color['text-muted']};
  font-size: ${tokens.type['label-size']};
  font-weight: ${tokens.type['body-weight']};
  line-height: ${tokens.type['label-line']};
`;

const clampShare = (progress: number): number => Math.min(Math.max(progress, 0), 1);

const HandoffResultState: React.FC<IHandoffResultStateProps> = ({
  status,
  body,
  meta,
  actions,
  footnote,
  progress,
  className
}) => {
  const { t } = useMultiLanguageTranslation();
  const titleId = useId();
  const rows = Array.isArray(meta) ? meta : [];
  const buttons = Array.isArray(actions) ? actions : [];
  const determinate = typeof progress === 'number' && Number.isFinite(progress);
  const share = determinate ? clampShare(progress) : 0;

  return (
    <Result className={className} data-status={status} data-testid="handoff-result-state">
      <IconBadge $status={status} aria-hidden="true" data-testid="handoff-result-state-icon">
        {status === 'sending' ? <Spinner data-testid="handoff-result-state-spinner" /> : null}
        {status === 'success' ? <>&#10003;</> : null}
        {status === 'failed' ? <>&#33;</> : null}
      </IconBadge>

      {/* The panel swaps states in place, so the outcome has to be announced, not just repainted. */}
      <Message role="status">
        <Title id={titleId} data-testid="handoff-result-state-title">
          {t(`${TCustomerInsights}.Handoff.Result.${COPY_KEY[status]}Title`)}
        </Title>
        <Body data-testid="handoff-result-state-body">
          {body || t(`${TCustomerInsights}.Handoff.Result.${COPY_KEY[status]}Body`)}
        </Body>
      </Message>

      {status === 'sending' ? (
        <Track
          role="progressbar"
          aria-labelledby={titleId}
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={determinate ? Math.round(share * 100) : undefined}
          data-indeterminate={!determinate}
          data-testid="handoff-result-state-progress"
        >
          <Fill $indeterminate={!determinate} $share={share} />
        </Track>
      ) : null}

      {rows.length > 0 ? (
        <Meta data-testid="handoff-result-state-meta">
          {rows.map(row => (
            <MetaRow key={row.label} data-testid="handoff-result-state-meta-row">
              <MetaLabel>{row.label}</MetaLabel>
              <MetaValue>{row.value}</MetaValue>
            </MetaRow>
          ))}
        </Meta>
      ) : null}

      {buttons.length > 0 ? (
        <Actions data-testid="handoff-result-state-actions">
          {buttons.map(action => (
            <ActionButton
              key={action.label}
              type="button"
              onClick={action.onClick}
              disabled={action.disabled}
              $tone={action.tone || 'secondary'}
              $hue={ACTION_HUE[status]}
              data-tone={action.tone || 'secondary'}
              data-testid="handoff-result-state-action"
            >
              {action.label}
            </ActionButton>
          ))}
        </Actions>
      ) : null}

      {footnote ? (
        <Footnote data-testid="handoff-result-state-footnote">{footnote}</Footnote>
      ) : null}
    </Result>
  );
};

export default HandoffResultState;
